<template>
  <div class="relative">
    <vs-button class="w-full" color="secondary" type="border" @click="onClickListen">
      <div class="flex items-center">
        <feather-icon icon="Volume2Icon" />
        <span class="ml-2">{{ $t('번역본 듣기') }}</span>
      </div>
    </vs-button>

    <!-- TTS 모드 표시 -->
    <div
      v-if="showAudio && isTTSMode"
      class="z-10 absolute top-full left-0 mt-2 flex items-center border rounded px-3 py-2 bg-white shadow gap-2"
    >
      <div class="flex items-center gap-2">
        <feather-icon icon="Volume2Icon" class="animate-pulse text-primary" />
        <span class="text-sm">{{ $t('음성으로 재생 중...') }}</span>
      </div>
      <div class="cursor-pointer" @click="closeAudio">
        <feather-icon icon="XIcon" />
      </div>
    </div>

    <!-- 오디오 파일 플레이어 -->
    <div
      v-if="showAudio && !isTTSMode"
      class="z-10 absolute top-full left-0 mt-2 flex items-center border rounded px-3 py-2 bg-white shadow gap-2"
    >
      <audio
        ref="audioPlayer"
        :src="audioSrc"
        controls
        @ended="showAudio = false"
      ></audio>
      <div class="cursor-pointer" @click="closeAudio">
        <feather-icon icon="XIcon" />
      </div>
    </div>
  </div>
</template>

<script>

import HttpRequest from "@/util/HttpRequest";
import * as UiUtil from "@/util/UiUtil";

export default {
  name: "ListenTranslation",
  props: {
    bodyComponent: Object,
    value: { type: String, default: "" },
  },
  data() {
    return {
      showAudio: false,
      audioSrc: undefined, // 오디오 파일의 경로를 지정합니다.
      isTTSMode: false, // TTS 모드인지 여부
      ttsText: '', // TTS로 재생할 텍스트
      ttsUtterance: null, // TTS Utterance 객체
      availableVoices: [], // TTS 음성 목록
    };
  },
  async mounted() {
    // TTS 음성 목록 로드
    await this.loadTTSVoices();
  },
  methods: {
    // TTS 음성 목록 로드 (AiTranslatePiece.vue 방식)
    async loadTTSVoices() {
      if (!window.speechSynthesis) {
        console.warn('Speech Synthesis API를 지원하지 않는 브라우저입니다.');
        return;
      }

      return new Promise((resolve) => {
        // 음성 목록이 이미 로드된 경우
        let voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          this.availableVoices = voices;
          console.log('TTS 음성 목록 로드 완료:', voices.length, '개');
          resolve();
          return;
        }

        // 음성 목록이 아직 로드되지 않은 경우 이벤트 리스너 등록
        const loadVoices = () => {
          voices = window.speechSynthesis.getVoices();
          this.availableVoices = voices;
          console.log('TTS 음성 목록 로드 완료:', voices.length, '개');
          resolve();
        };

        window.speechSynthesis.addEventListener('voiceschanged', loadVoices, { once: true });

        // 일정 시간 후에도 로드되지 않으면 타임아웃
        setTimeout(() => {
          voices = window.speechSynthesis.getVoices();
          this.availableVoices = voices;
          console.log('TTS 음성 목록 타임아웃 로드:', voices.length, '개');
          resolve();
        }, 1000);
      });
    },
    async onClickListen() {
      if (this.showAudio === false) {
        if (this.bodyComponent) {
          await this.loaded();
        }
      }
    },
    closeAudio () {
      if (this.isTTSMode) {
        // TTS 중지
        if (this.ttsUtterance) {
          speechSynthesis.cancel();
        }
      } else {
        // 오디오 파일 중지
        this.$refs.audioPlayer.pause();
      }
      this.showAudio = false;
      this.isTTSMode = false;
    },
    // TTS 지원 여부 확인 (언어 코드 형식 정규화)
    isTTSSupported(lang) {
      // 모바일 기기인 경우 TTS 대신 오디오 파일 사용을 위해 false 반환
      if (this.$viewport.isMobile) {
        return false;
      }

      if (!window.speechSynthesis) {
        return false;
      }

      // 현재 언어에 대한 TTS 지원 여부 확인
      const voices = this.availableVoices;
      if (voices.length === 0) {
        return false;
      }

      // 현재 언어코드와 매칭되는 음성이 있는지 확인 (하이픈과 언더스코어 둘 다 처리)
      const currentLangCode = lang.split(/[-_]/)[0]; // 'ko-KR' 또는 'ko_KR' → 'ko'

      const hasMatchingVoice = voices.some(voice => {
        const voiceLang = voice.lang.split(/[-_]/)[0]; // 음성 쪽도 하이픈/언더스코어 둘 다 처리
        return voiceLang === currentLangCode;
      });

      return hasMatchingVoice;
    },
    // TTS로 텍스트 재생 (언어 코드 형식 정규화)
    playTextWithTTS(text, lang) {
      if (!('speechSynthesis' in window)) {
        console.error('TTS가 지원되지 않는 브라우저입니다.');
        return;
      }

      // 기존에 재생 중인 음성이 있으면 중단
      window.speechSynthesis.cancel();

      const msg = new SpeechSynthesisUtterance();
      msg.text = text;

      // 언어 코드 정규화: 언더스코어를 하이픈으로 변환 (ja_JP → ja-JP)
      const normalizedLang = lang.replace('_', '-');
      msg.lang = normalizedLang;

      msg.onend = () => {
        console.log('TTS 재생 완료');
        this.showAudio = false;
        this.isTTSMode = false;
      };

      msg.onerror = (error) => {
        console.error('TTS 재생 실패:', error);
        this.showAudio = false;
        this.isTTSMode = false;
      };

      this.ttsUtterance = msg;
      window.speechSynthesis.speak(msg);
    },
    async loaded() {
      try {
        UiUtil.loading(true);
        const json = this.bodyComponent.translationMap()
        const toLang = this.$i18n.locale

        // TTS 지원 여부 확인
        const isTTSAvailable = this.isTTSSupported(toLang);

        if (isTTSAvailable) {
          // TTS 지원하는 경우: 텍스트로 요청
          const option = {
            origin: JSON.stringify(json),
            lang: toLang,
            enable_audio: false,
          }

          await HttpRequest.post('/api/v1/miras/speech', option)
            .then(({ data }) => {
              // data는 JSON { narrationText, lang, enableAudio }
              console.log('TTS 응답 데이터:', data);
              this.ttsText = data.narrationText; // data.data.narrationText → data.narrationText
              this.isTTSMode = true;
              this.showAudio = true;

              this.$nextTick(() => {
                this.playTextWithTTS(this.ttsText, toLang);
              });
            })
            .catch(err => {
              console.error('TTS 요청 오류:', err);
            });

        } else {
          // TTS 지원하지 않는 경우: 오디오 파일로 요청
          const option = {
            responseType: 'blob',
            origin: JSON.stringify(json),
            lang: toLang,
            enable_audio: true,
          }

          await HttpRequest.post('/api/v1/miras/speech', option)
            .then(({ data }) => {
              // data는 Blob(audio/mpeg)
              this.audioSrc = URL.createObjectURL(data);
              this.isTTSMode = false;
              this.showAudio = true;

              this.$nextTick(() => {
                this.$refs.audioPlayer.play();
              });
            })
            .catch(err => {
              console.error('오디오 요청 오류:', err);
            });
        }

      } catch (err) {
        console.log("loaded try-catch", err)
      }
      UiUtil.loading(false);
    },
  },
  beforeDestroy() {
    // TTS 재생 중지
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }
}

</script>

<style scoped lang="scss">

</style>
